import { SelectionList, Tooltip } from '@dixa/lib-shared-habla';
import Fuse from 'fuse.js';
import {
  SearchFilterConditionCustomAttribute,
  SearchFilterConditionCustomAttributeField,
} from 'lib-search-types';
import { filterOperatorCatalogue, SearchFilterLayout } from 'lib-search-ui';
import orderBy from 'lodash/orderBy';
import { memo, useCallback, useMemo } from 'react';

import { CustomFieldAttribute, customFieldsApi } from 'apis/customFields';
import { customAttributeTypeAttributes } from 'app/views/layout/views/settings/views/customAttributes/components/table/utils/customAttributeTypeAttributes';

import styles from '../../filterAddCondition.module.scss';

type FilterAddConditionCustomAttributesProps = {
  /** Whether the parent dropdown is open. */
  isOpen: boolean;
  /** The current search term. */
  debouncedSearchTerm: string;
};

export const FilterAddConditionCustomAttributes = memo(
  function FilterAddConditionCustomAttributes({
    debouncedSearchTerm,
    isOpen,
  }: FilterAddConditionCustomAttributesProps): JSX.Element | null {
    const { customAttributes, isLoading } =
      customFieldsApi.useReadAttributesQuery(
        { entityId: 'conversation' },
        {
          skip: !isOpen,
          selectFromResult: ({ data, ...rest }) => ({
            ...rest,
            customAttributes: data?.data.attributes ?? [],
          }),
        },
      );

    const getCustomAttributeFilterConditionValue = useCallback(
      (
        attribute: CustomFieldAttribute,
      ): SearchFilterConditionCustomAttribute => {
        const field: SearchFilterConditionCustomAttributeField = `conversation_custom_attribute`;

        return {
          field,
          operator:
            filterOperatorCatalogue.conversations[field].defaultOperator,
          values: [],
          attributeId: attribute.id,
        };
      },
      [],
    );

    /**
     * The list of custom attribute available to filter by.
     *
     * These are first filtered according to the search criteria if any, and
     * finally ordered so that deactivated attributes are at the bottom.
     */
    const filteredCustomAttributes = useMemo(() => {
      const fuse = new Fuse<CustomFieldAttribute>(customAttributes, {
        keys: ['label', 'identifier'],
        threshold: 0.2,
        distance: 200,
      });

      const filteredAttributes = [
        ...(debouncedSearchTerm
          ? fuse.search(debouncedSearchTerm).map(({ item }) => item)
          : customAttributes),
      ];

      return orderBy<CustomFieldAttribute>(
        filteredAttributes,
        'is_deactivated',
        'asc',
      );
    }, [customAttributes, debouncedSearchTerm]);

    if (isLoading) return <SearchFilterLayout.Loading />;
    if (!filteredCustomAttributes.length) return null;

    return (
      <SelectionList.Section<SearchFilterConditionCustomAttribute>
        key="custom-attributes"
        title="Conversation attributes"
      >
        {filteredCustomAttributes.map((attribute) => (
          <Tooltip
            className={styles.tooltip}
            key={attribute.id}
            head={attribute.label}
            content={attribute.description}
            disabled={!attribute.description}
            side="left"
            wrap="none"
          >
            <SearchFilterLayout.Item<SearchFilterConditionCustomAttribute>
              value={getCustomAttributeFilterConditionValue(attribute)}
              label={attribute.label}
              icon={customAttributeTypeAttributes[attribute.type].icon}
              highlight={debouncedSearchTerm}
              noFixedSelectionStateSpace
              textProps={{
                color: attribute.is_deactivated ? 'tertiary' : undefined,
              }}
            />
          </Tooltip>
        ))}
      </SelectionList.Section>
    );
  },
);
